import {
  HTMLAttributes,
  isValidElement,
  type MouseEvent,
  ReactElement,
  useCallback,
  useId,
  useMemo,
  useState,
} from "react";
import { useControlledValue, useLongPress } from "../../hooks";
import { cn, composeEventHandlers, isTouchDevice, Slot } from "../../utils";
import type { DropdownInteraction } from "../Dropdown";
import { Dropdown } from "../Dropdown";
import type { TooltipProps } from "./types";

export const Tooltip = (props: TooltipProps) => {
  const {
    trigger,
    children,
    content,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    defaultIsOpen,
    placement = "top",
    offset = 8,
    maxWidth = 400,
    contentClassName,
    disabled,
    // Dropdown-specific props (extract explicitly)
    hoverOptions,
    portal,
    // Forward remaining props (from parent wrappers like Popover/Menu)
    ...forwardedProps
  } = props;

  const isMobile = useMemo(isTouchDevice, []);
  const [wasClicked, setWasClicked] = useState<boolean>(false);

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: defaultIsOpen || false,
  });

  const longPress = useLongPress({
    delay:
      typeof hoverOptions?.delay === "number"
        ? hoverOptions?.delay
        : typeof hoverOptions?.delay === "function"
          ? 500
          : hoverOptions?.delay?.open || 500,
    onLongPress: () => {
      onOpenChange(true);
    },
  });

  const generatedId = useId();
  const triggerId = children.props.id || `${generatedId}-trigger`;
  const contentId =
    isValidElement(content) &&
    (content as ReactElement<HTMLAttributes<HTMLElement>>).props.id
      ? (content as ReactElement<HTMLAttributes<HTMLElement>>).props.id
      : `${generatedId}-content`;

  const triggerElementBase = trigger || children;

  const handleClick = useCallback(() => {
    setWasClicked(true);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleBlur = useCallback(() => {
    setWasClicked(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setWasClicked(false);
  }, []);

  const mobileHandlers = useMemo(() => {
    if (!isMobile) {
      return {};
    }

    return {
      onTouchStart: longPress.onTouchStart,
      onTouchEnd: longPress.onTouchEnd,
      onTouchCancel: longPress.onTouchCancel,
    };
  }, [isMobile, longPress]);

  const { onClick: forwardedOnClick, ...restForwardedProps } =
    forwardedProps as Record<string, unknown>;

  const composedOnClick = composeEventHandlers(
    forwardedOnClick as ((e: MouseEvent) => void) | undefined,
    handleClick,
  );

  const triggerElement = (
    <Slot
      {...restForwardedProps}
      {...(open ? { "aria-describedby": contentId } : {})}
      onClick={composedOnClick}
      onBlur={handleBlur}
      onMouseLeave={handleMouseLeave}
      {...mobileHandlers}
    >
      {triggerElementBase}
    </Slot>
  );

  const interactions: DropdownInteraction[] = isMobile
    ? ["dismiss"]
    : ["hover", "focus", "dismiss"];

  return (
    <Dropdown
      trigger={triggerElement}
      content={content}
      triggerId={triggerId}
      contentId={contentId}
      open={open}
      onOpenChange={onOpenChange}
      interactions={interactions}
      role="tooltip"
      placement={placement}
      offset={offset}
      maxWidth={maxWidth}
      floatingFocusManager={{
        modal: false,
        initialFocus: -1,
        returnFocus: false,
        restoreFocus: false,
      }}
      contentClassName={cn("p-2", contentClassName)}
      hoverOptions={
        hoverOptions || {
          delay: {
            open: 500,
            close: 0,
          },
        }
      }
      disabled={disabled || wasClicked}
      portal={portal}
    />
  );
};
